// Представление «Кому выдано»: реестр выдач из кассы за месяц и их сумма.
//
// Гендиректор заказчика 29.09.2026: суточные расходы больше не ведутся в Excel, «я хочу видеть
// их так же прозрачно и наглядно»; уточнение финдиректора: «что-то ему дали и куда он их
// потратил». Вторая половина вопроса, «куда потратил», отвечена вкладкой «На что потрачено»;
// эта вкладка отвечает на первую: кому и сколько дали. Тз 5.6 обещало «реестр выдач и сводку по
// категориям (аналог листа «итог»)», а реестра выдач за период в программе не было.
//
// Это лист «Получено в подотчёт» из книги заказчика: день, кому, сколько, а сверху итог за
// месяц. «Остаток с апреля» стоит в нём строкой со знаком минус, и здесь тоже: в книге это
// тоже строка прихода (компания должна человеку).
//
// Итог считает сервер по той же выборке, что и строки. Складывать суммы на экране нельзя: на
// первом сторно сложенное разошлось бы с «Выдано» в сверке соседней вкладки, а это два разных
// ответа на один вопрос.
//
// Месяц по умолчанию: последний, где есть выдачи (не позже текущего). Вкладка открывается без
// месяца в адресе, а майская книга лежит в мае: на текущем месяце человек видел «выдач не было» и
// листал назад пять раз. Сервер отвечает ближайшим месяцем с выдачами (`latest_if_empty`), а экран
// говорит об этом одной строкой над блоками: подменить месяц молча значило бы выдать май за
// октябрь. Месяц, выбранный человеком, главнее: он записан в адрес, и подсказка к нему не
// применяется. Выдач нет нигде: остаётся текущий месяц, и экран говорит об этом, а не выдумывает:
// прошлые месяцы переносятся из книг Excel, и пока книгу за месяц не переносили, строк не будет.
// Листать месяц можно стрелками; месяц живёт в адресе и переходит на «На что потрачено» вместе
// с человеком.

import { RiHandCoinLine, RiWallet3Line } from '@remixicon/react';

import type { IssueOut } from '@/shared/api/endpoints/accountable';
import { exportCashFlow } from '@/shared/api/endpoints/reports';
import type { ApiError } from '@/shared/errors/api-error';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { ExportButton } from '@/shared/reports/ExportButton';
import { PrintButton } from '@/shared/reports/PrintButton';
import { EMPTY_SEARCH, type SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDataAsOf, formatDateOnly, parseMoment } from '@/shared/time/format';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдреса,
  parsePeriod,
  shiftPeriod,
} from '@/shared/time/period';
import { Block } from '@/shared/ui/block';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { Metric } from '@/shared/ui/metric';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';
import { cn } from '@/utils/cn';

import { ПоказанПоследнийМесяц, ЧТО_ВЫДАЧИ } from '../components/ПоказанПоследнийМесяц';
import { ПустойМесяц } from '../components/ПустойМесяц';
import { подписьОтбора } from '../components/подпись-отбора';
import { METHOD_TITLES } from '../dictionaries';
import { useIssues, usePersons } from '../queries';
import { FILTER_ENTITY, FILTER_PERIOD, FILTER_PERSON, personPath } from '../route';

/** «1 выдача», «48 выдач», «2 выдачи». */
const ВЫДАЧИ = ['выдача', 'выдачи', 'выдач'] as const;

/** «2026-05-01» → месяц и год. Данные несут дату начала месяца, а не его название. */
function месяцИзДаты(iso: string | null): { year: number; month: number } | null {
  const совпадение = iso === null ? null : /^(\d{4})-(\d{2})/.exec(iso);
  return совпадение === null || совпадение === undefined
    ? null
    : { year: Number(совпадение[1]), month: Number(совпадение[2]) };
}

function слова(текст: string): string[] {
  return текст
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/[^0-9a-zа-я]+/u)
    .filter((слово) => слово !== '');
}

/**
 * Подпись строки под именем: «12.05.2026 · наличными · назначение».
 *
 * Назначение опускается, когда оно лишь повторяет имя получателя. Книга заказчика пишет в
 * колонке «Наименование заявки» фамилию того, кому выдали («Шатун»), и перенос кладёт её в
 * назначение: строка «Шатун Андрей» с подписью «… · Шатун» повторяла бы имя второй раз. Всё,
 * что не совпадает с именем («Остаток с Апреля», «на хозяйственные нужды»), остаётся.
 */
export function подписьВыдачи(выдача: IssueOut): string {
  // День по поясу объектов, как и граница месяца у выборки: дата, прочитанная из момента в UTC
  // текстом, для ночной выдачи показала бы вчерашний день прямо в списке следующего месяца.
  const части = [
    formatDateOnly(выдача.issued_on ?? выдача.issued_at),
    METHOD_TITLES[выдача.method],
  ];
  const назначение = (выдача.purpose ?? '').trim();
  if (назначение !== '') {
    const имя = слова(выдача.employee_name);
    const повторяетИмя = слова(назначение).every((слово) =>
      имя.some((своё) => своё.startsWith(слово)),
    );
    if (!повторяетИмя) {
      части.push(назначение);
    }
  }
  return части.join(' · ');
}

export function CashIssues({
  search,
  onFilter,
  onClearFilters,
}: {
  search: SearchState;
  onFilter: (key: string, value: string | null) => void;
  /** Снять несколько отборов разом: по одному они затирали бы друг друга в адресе. */
  onClearFilters: (keys: readonly string[]) => void;
}) {
  // Месяц, выбранный человеком (стрелкой, ссылкой, кнопкой), записан в адресе и главнее любой
  // подсказки. Без него запрашивается текущий, а флаг просит сервер: если в нём выдач нет,
  // ответить ближайшим более ранним месяцем, где они есть.
  const заданный = месяцИзАдреса(search.filters[FILTER_PERIOD]);
  const сегодня = parsePeriod(undefined);
  const запрошенный = заданный ?? сегодня;
  const юрлицо = search.filters[FILTER_ENTITY];
  const лицо = search.filters[FILTER_PERSON];
  const выдачи = useIssues({
    year: запрошенный.year,
    month: запрошенный.month,
    legal_entity_id: юрлицо,
    person_id: лицо,
    latest_if_empty: заданный === null ? true : undefined,
  });
  // Месяц, данные которого на экране, берётся из ответа, а не из адреса: без месяца в адресе
  // сервер мог ответить и другим. Пока ответа нет, а месяца в адресе тоже нет, какой из них
  // покажут, неизвестно, и календарь стоит с заглушкой (текущий месяц, подставленный «пока едет»,
  // на ответе сменился бы на другой). Ошибка заглушку не держит: ответа уже не будет.
  const месяцДанных = месяцИзДаты(выдачи.data?.period_from ?? null);
  // Пока едет другой месяц, на экране остаётся прежний срез (правило 11), но приглушённый: его не
  // принять за срез выбранного месяца. ПУСТОЙ прежний срез не остаётся вовсе: «За сентябрь выдач не
  // было» под календарём «Май 2026» читалось бы как ответ про май.
  const устарело = выдачи.isPlaceholderData;
  const данныеЭкрана =
    устарело && выдачи.data !== undefined && выдачи.data.items.length === 0
      ? undefined
      : выдачи.data;
  // Выбранный человеком месяц пуст: где ближайшие выдачи? Тот же флаг, что открывает вкладку на
  // последнем месяце с данными, но запрашивается только для пустого месяца без отборов, чтобы
  // главная кнопка пустого экрана вела прямо к ним, а не к соседнему пустому месяцу. Прежний срез
  // не в счёт: по нему второй запрос уходил бы при каждом шаге на непустой месяц.
  const пустойВыбранный =
    заданный !== null &&
    выдачи.data !== undefined &&
    !устарело &&
    выдачи.data.items.length === 0 &&
    юрлицо === undefined &&
    лицо === undefined;
  const ближайшиеВыдачи = useIssues(
    {
      year: запрошенный.year,
      month: запрошенный.month,
      legal_entity_id: юрлицо,
      person_id: лицо,
      latest_if_empty: true,
    },
    пустойВыбранный,
  );
  const ближайший = пустойВыбранный ? месяцИзДаты(ближайшиеВыдачи.data?.period_from ?? null) : null;
  const ближайшийС =
    ближайший !== null &&
    (ближайшиеВыдачи.data?.items.length ?? 0) > 0 &&
    (ближайший.year !== запрошенный.year || ближайший.month !== запрошенный.month)
      ? ближайший
      : null;
  const месяцНеизвестен = заданный === null && выдачи.data === undefined && !выдачи.isError;
  const период = заданный ?? месяцДанных ?? сегодня;
  const юрлица = useLegalEntitiesQuery();
  // Все подотчётные лица, а не только действующие: реестр выдач берёт и тех, у кого подотчётность
  // уже снята, и выдачу ушедшему человеку нужно уметь отобрать. Без этого отбор по нему в адресе
  // стоял бы, а поле выбора показывало бы «все».
  const лица = usePersons({ active_only: false });

  const сдвинуть = (на: number) =>
    onFilter(FILTER_PERIOD, formatPeriodKey(shiftPeriod(период, на)));
  const естьОтбор = юрлицо !== undefined || лицо !== undefined;
  // Отбор словами: шапка на печать не идёт, и итог без этой подписи читался бы как итог месяца
  // целиком, хотя отобрано одно юрлицо или один человек.
  const выбранноеЮрлицо = юрлица.data?.items.find((строка) => строка.id === юрлицо);
  const отбор = подписьОтбора([
    выбранноеЮрлицо?.short_name ?? выбранноеЮрлицо?.name,
    лица.data?.items.find((строка) => строка.id === лицо)?.employee_name,
  ]);

  return (
    <main className='flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-bg-weak-50'>
      <Toolbar
        actions={
          // Та же книга, что и на «На что потрачено»: сверка месяца, разрез по статьям и, на
          // отдельном листе, этот самый реестр выдач. Файл равен экрану, а не пересказывает
          // соседний.
          <ExportButton
            собрать={() =>
              exportCashFlow({
                year: период.year,
                month: период.month,
                legal_entity_id: юрлицо,
                accountable_person_id: лицо,
              })
            }
          />
        }
      >
        <PeriodStepper
          label='Месяц выдач'
          title={месяцНеизвестен ? null : formatPeriodTitle(период)}
          onPrev={() => сдвинуть(-1)}
          onNext={() => сдвинуть(1)}
        />
        <div className='min-w-56'>
          <SelectField
            id='issues-entity'
            label='Юрлицо'
            value={юрлицо ?? NO_VALUE}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            noneLabel='все юрлица'
            placeholder='все юрлица'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_ENTITY, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <div className='min-w-56'>
          <SelectField
            id='issues-person'
            label='Кому'
            value={лицо ?? NO_VALUE}
            options={(лица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.employee_name,
            }))}
            noneLabel='все'
            placeholder='все'
            pending={лица.isPending}
            failed={лица.isError}
            failedError={лица.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_PERSON, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <PrintButton />
      </Toolbar>

      {/* Ширина чтения ограничена тем же потолком, что у соседней вкладки: при переключении
          между «Кому выдано» и «На что потрачено» колонка не должна менять ширину. */}
      <div data-печать='документ' className='flex-1 overflow-y-auto p-5'>
        <div className='flex max-w-metrics flex-col gap-5'>
          <ScreenState
            data={данныеЭкрана}
            error={выдачи.error as ApiError | null}
            subject='выдачи под отчёт'
            isEmpty={(данные) => данные.items.length === 0}
            onRetry={() => void выдачи.refetch()}
            // Заглушка повторяет форму будущего: блок с итогом и список строк под ним.
            loading={
              <div className='flex flex-col gap-5'>
                <Skeleton className='h-28 w-full' />
                <Block flush title='Кому выдано' icon={RiHandCoinLine}>
                  <ListSkeleton rows={6} blockInset />
                </Block>
              </div>
            }
            empty={
              <ПустойМесяц
                месяц={месяцДанных ?? запрошенный}
                естьОтбор={естьОтбор}
                снять={() => onClearFilters([FILTER_ENTITY, FILTER_PERSON])}
                назад={() => сдвинуть(-1)}
                ближайший={ближайшийС}
                кБлижайшему={() =>
                  ближайшийС !== null && onFilter(FILTER_PERIOD, formatPeriodKey(ближайшийС))
                }
                // Месяц записывается явно: «К текущему месяцу» обещает именно его, а без месяца в
                // адресе вкладка ушла бы искать последний, где есть выдачи.
                кТекущему={() => onFilter(FILTER_PERIOD, formatPeriodKey(сегодня))}
                значок={RiWallet3Line}
                картинка='деньги'
                заголовокПусто={(месяц) => `За ${месяц} выдач не было`}
                // До 06.10.2026 здесь обещали перенос из книг Excel. После очистки данных
                // заказчик вносит месяцы сам, и обещание звало бы ждать того, что не придёт.
                описаниеПусто='Выдача появится здесь, как только её оформят: на вкладке «Подотчёт» в карточке человека кнопкой «Оформить выдачу» (дату можно поставить прошлую) или в телефоне по просьбе о деньгах.'
                заголовокОтбор='Под этот отбор выдач нет'
                описаниеОтбор='За этот месяц по выбранному юрлицу или человеку выдач не нашлось. Снимите отбор, чтобы посмотреть выдачи по всем.'
              />
            }
          >
            {(данные) => {
              const срез = parseMoment(данные.generated_at);
              // Месяц берётся из САМИХ данных, а не из адреса. Пока едет новый месяц, на экране
              // остаётся прошлый срез (правило 11), и подпись из адреса стояла бы рядом с суммой
              // другого месяца — то есть врала.
              const месяц = formatPeriodTitle(
                месяцИзДаты(данные.period_from) ?? период,
              ).toLowerCase();
              return (
                <div
                  aria-busy={устарело}
                  className={cn('flex flex-col gap-5 transition-opacity', устарело && 'opacity-60')}
                >
                  {заданный === null && (
                    <ПоказанПоследнийМесяц
                      показан={месяцДанных ?? запрошенный}
                      запрошен={сегодня}
                      что={ЧТО_ВЫДАЧИ}
                    />
                  )}
                  <Block
                    icon={RiWallet3Line}
                    title={`Выдано за ${месяц}`}
                    note={[срез === null ? undefined : formatDataAsOf(срез), отбор]
                      .filter((часть) => часть !== undefined)
                      .join(' · ')}
                  >
                    <Metric
                      tone='главный'
                      label='Выдано за месяц'
                      // С рублём: рядом стоит число выдач, и без знака «48 выдач» и «13 311 851»
                      // читались бы как две величины одной единицы.
                      value={formatRubles(данные.total_amount)}
                      note={countLabel(данные.total, ВЫДАЧИ)}
                    />
                  </Block>
                  <Block flush title='Кому выдано' icon={RiHandCoinLine}>
                    <div>
                      {данные.items.map((строка) => (
                        <ListRow
                          key={строка.id}
                          // Строка ведёт в карточку того, кому выдали, на вкладку «Выдачи»: там
                          // эту запись можно отменить. Решение о деньгах принимается в карточке,
                          // а не в реестре.
                          to={personPath(строка.accountable_person_id, {
                            ...EMPTY_SEARCH,
                            tab: 'issues',
                          })}
                          selected={false}
                          blockInset
                          title={строка.employee_name}
                          subtitle={подписьВыдачи(строка)}
                          // Без знака рубля: столбец целиком денежный, единицу называет
                          // заголовок блока. «Остаток с апреля» идёт строкой со знаком минус
                          // как есть: в книге заказчика это тоже строка прихода.
                          value={formatKopecks(строка.amount)}
                        />
                      ))}
                    </div>
                    <p className='px-5 py-3 text-paragraph-xs text-text-sub-600'>
                      Передачи между подотчётными лицами: в карточке человека, вкладка «Передачи»
                    </p>
                  </Block>
                </div>
              );
            }}
          </ScreenState>
        </div>
      </div>
    </main>
  );
}
